/**
 * Editor для Акту виробничих послуг (Порція Виробництво.4b).
 *
 * Найпростіший виробничий editor — без матеріальної частини, лише header
 * + одна табличка послуг. Patterns точно як у GoodsSaleEditor:
 * - id='new' для CREATE, UUID для EDIT
 * - useNumberPreview placeholder для нового документа
 * - usePermissionsByOrg('production.service_act.manage')
 * - disabled={isPosted} для всіх полів і таблиці
 * - RequirePermission навколо кнопок Save/Post/Unpost
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, contractors as contractorsApi } from '@/api/catalogs';
import { accounts as accountsApi } from '@/api/documents';
import {
  productionServiceActs,
  type ProductionServiceActItemIn,
} from '@/api/productionServiceActs';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import { ProjectField } from '@/components/ProjectSelect';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import DocLayout from '@/shared/components/DocLayout';
import QuickCreateLink from '@/shared/components/QuickCreateLink';
import QuickContractModal from '@/shared/components/QuickContractModal';
import QuickContractorModal from '@/shared/components/QuickContractorModal';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { taxInvoicesOutgoing } from '@/api/taxInvoices';
import { customerOrders } from '@/api/orders';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import QuickNomenclatureModal from '@/shared/components/QuickNomenclatureModal';
import type { GoodsBalanceRow } from '@/api/documents';
import { refs as refsApi } from '@/api/refs';
import SearchSelect from '@/components/SearchSelect';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { useConfirm } from '@/components/ConfirmProvider';
import { contractLabel } from '@/shared/utils/labels';
import TchTable from '@/shared/components/TchTable';

const PERMISSION = 'production.service_act.manage';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  customer_contractor_id: string;
  contract_id: string;
  advance_offset_mode: 'AUTO' | 'NONE';
  customer_order_id: string;
  department_id: string;
  service_amount: string;
  vat_amount: string;
  revenue_account_code: string | null;
  receivable_account_code: string | null;
  cost_account_code: string | null;
  comment: string;
  project_id: string | null;
  items: ProductionServiceActItemIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  customer_contractor_id: '',
  contract_id: '',
  advance_offset_mode: 'AUTO',
  customer_order_id: '',
  department_id: '',
  service_amount: '0',
  vat_amount: '0',
  revenue_account_code: null,
  receivable_account_code: null,
  cost_account_code: null,
  comment: '',
  project_id: null,
  items: [],
};

const newRow = (n: number): ProductionServiceActItemIn => ({
  line_number: n,
  nomenclature_id: '',
  quantity: '1',
  price: '0',
  description: '',
  vat_rate_percent: null,
  vat_amount: '0',
  tax_purpose_id: null,
  planned_amount: null,
  nomenclature_group_id: null,
});

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

/** ч.862: іконка документа для кнопок «заповнити з …» / «створити …». */
function DocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden
      style={{ flex: '0 0 auto' }}>
      <path d="M9.5 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5l-3.5-3.5Z"
        stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

export default function ProductionServiceActEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [urlParams] = useSearchParams();
  const basisId = urlParams.get('basis');
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION);
  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const accountsQ = useQuery({
    queryKey: ['accounts-postable'],
    queryFn: () => accountsApi.list({ only_postable: true, limit: 1000 }),
  });
  const acctList = accountsQ.data ?? [];

  const docQuery = useQuery({
    queryKey: ['production-service-act', id],
    queryFn: () => productionServiceActs.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  // ч.698 (візуал goods-sales): вкладки канон seg-tabs.
  const [viewTab, setViewTab] = useState<'lines' | 'accounts' | 'tax'>('lines');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [quickNomOpen, setQuickNomOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const numberPreview = useNumberPreview(
    'doc.production_service_acts',
    form.organization_id,
    isNew,
  );
  const taxPurposesQ = useQuery({ queryKey: ['tax-purposes'], queryFn: refsApi.taxPurposes });
  // ч.706: підрозділ котла 23 (виробничий контур послуг).
  const depsQ = useQuery({
    queryKey: ['departments', form.organization_id],
    queryFn: () => import('@/api/hr').then((m) =>
      m.departmentsApi.list({ organization_id: form.organization_id, limit: 500 })),
    enabled: !!form.organization_id,
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.customer_contractor_id],
    queryFn: () => refsApi.contracts({
      organization_id: form.organization_id,
      contractor_id: form.customer_contractor_id,
    }),
    enabled: !!form.organization_id && !!form.customer_contractor_id,
  });

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        customer_contractor_id: d.customer_contractor_id,
        contract_id: d.contract_id ?? '',
        advance_offset_mode: d.advance_offset_mode ?? 'AUTO',
        customer_order_id: d.customer_order_id ?? '',
        department_id: d.department_id ?? '',
        service_amount: d.service_amount,
        vat_amount: d.vat_amount,
        revenue_account_code: d.revenue_account_code ?? null,
        receivable_account_code: d.receivable_account_code ?? null,
        cost_account_code: d.cost_account_code ?? null,
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        items: (() => {
          const rows = d.items.map((it) => ({
            line_number: it.line_number,
            nomenclature_id: it.nomenclature_id,
            quantity: it.quantity ?? '1',
            price: it.price,
            description: it.description ?? '',
            vat_rate_percent: it.vat_rate_percent ?? null,
            vat_amount: it.vat_amount ?? '0',
            tax_purpose_id: it.tax_purpose_id ?? null,
            planned_amount: it.planned_amount ?? null,
            nomenclature_group_id: it.nomenclature_group_id ?? null,
          }));
          // ч.699: legacy-док (ПДВ у шапці, рядки без ПДВ) — розкласти по
          // рядках: ставка-хіт (20/14/7) або пропорційно з хвостом на останній.
          const svc = Number(d.service_amount) || 0;
          const vat = Number(d.vat_amount) || 0;
          const linesVat = rows.reduce((a, r) => a + (Number(r.vat_amount) || 0), 0);
          if (!d.posted && svc > 0 && vat > 0 && linesVat === 0 && rows.length > 0) {
            const ratio = vat / svc * 100;
            const hit = [20, 14, 7].find((c) => Math.abs(ratio - c) < 0.5);
            let acc = 0;
            rows.forEach((r, k) => {
              const amt = (Number(r.quantity) || 0) * (Number(r.price) || 0);
              const v = k === rows.length - 1
                ? vat - acc
                : Math.round((hit != null ? amt * hit / 100 : vat * (svc ? amt / svc : 0)) * 100) / 100;
              acc += v;
              r.vat_rate_percent = hit != null ? String(hit) : null;
              r.vat_amount = v.toFixed(2);
            });
          }
          return rows;
        })(),
      });
    }
  }, [isNew, docQuery.data]);

  const isPosted = docQuery.data?.posted ?? false;
  // ч.948 (рішення власника): «створити ↗» біля контрагента і договору
  // відкриває ПОПАП просто на сторінці документа (як у ПКО/РКО).
  const [quickContractor, setQuickContractor] = useState(false);
  const [quickContract, setQuickContract] = useState(false);
  const contractorNameForQuick = (cQ.data?.items ?? []).find((c: { id: string; name: string }) => c.id === form.customer_contractor_id)?.name ?? '';

  const acctItems = acctList.map((a) => ({ id: a.code, label: `${a.code} — ${a.name}`, hint: null }));
  const acctSelect = (
    value: string | null | undefined,
    onChange: (v: string | null) => void,
    def: string,
  ) => (
    <SearchSelect items={acctItems} value={value ?? ''}
      onChange={(v) => onChange(v || null)} disabled={isPosted}
      placeholder={`— (деф. ${def}) —`} />
  );

  // ч.698/699: суми — З РЯДКІВ (як у реалізації), ПДВ по-рядково;
  // header service_amount/vat_amount синхронізується (постинг як був).
  const lineAmount = (it: ProductionServiceActItemIn) =>
    (Number(it.quantity) || 0) * (Number(it.price) || 0);
  const rowsTotal = useMemo(
    () => form.items.reduce((acc, it) => acc + lineAmount(it), 0),
    [form.items],
  );
  const rowsVat = useMemo(
    () => form.items.reduce((acc, it) => acc + (Number(it.vat_amount) || 0), 0),
    [form.items],
  );
  const totals = useMemo(() => {
    const svc = form.items.length > 0 ? rowsTotal : (Number(form.service_amount) || 0);
    const vat = form.items.length > 0 ? rowsVat : (Number(form.vat_amount) || 0);
    return { svc, vat, total: svc + vat };
  }, [form.items.length, rowsTotal, rowsVat, form.service_amount, form.vat_amount]);
  useEffect(() => {
    if (isPosted || form.items.length === 0) return;
    const svcStr = rowsTotal.toFixed(2);
    const vatStr = rowsVat.toFixed(2);
    if (form.service_amount !== svcStr || form.vat_amount !== vatStr) {
      setForm((f) => ({ ...f, service_amount: svcStr, vat_amount: vatStr }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsTotal, rowsVat]);

  const create = useMutation({
    mutationFn: () =>
      productionServiceActs.create({
        organization_id: form.organization_id,
        document_number: form.document_number || null,
        document_date: new Date(form.document_date).toISOString(),
        customer_contractor_id: form.customer_contractor_id,
        contract_id: form.contract_id || null,
        advance_offset_mode: form.advance_offset_mode,
    customer_order_id: form.customer_order_id || null,
        service_amount: form.service_amount,
        vat_amount: form.vat_amount,
        revenue_account_code: form.revenue_account_code,
        receivable_account_code: form.receivable_account_code,
        cost_account_code: form.cost_account_code,
        comment: form.comment || null,
        project_id: form.project_id,
        items: form.items,
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['production-service-acts'] });
      navigate(`/production-service-acts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });

  const update = useMutation({
    mutationFn: () =>
      productionServiceActs.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        customer_contractor_id: form.customer_contractor_id,
        contract_id: form.contract_id || null,
        advance_offset_mode: form.advance_offset_mode,
    customer_order_id: form.customer_order_id || null,
        service_amount: form.service_amount,
        vat_amount: form.vat_amount,
        revenue_account_code: form.revenue_account_code,
        receivable_account_code: form.receivable_account_code,
        cost_account_code: form.cost_account_code,
        comment: form.comment || null,
        project_id: form.project_id,
        items: form.items,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-service-act', id] });
      qc.invalidateQueries({ queryKey: ['production-service-acts'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  // Новий: створити і одразу провести (create → post → картка).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await productionServiceActs.create({
        organization_id: form.organization_id,
        document_number: form.document_number || null,
        document_date: new Date(form.document_date).toISOString(),
        customer_contractor_id: form.customer_contractor_id,
        contract_id: form.contract_id || null,
        advance_offset_mode: form.advance_offset_mode,
    customer_order_id: form.customer_order_id || null,
        service_amount: form.service_amount,
        vat_amount: form.vat_amount,
        revenue_account_code: form.revenue_account_code,
        receivable_account_code: form.receivable_account_code,
        cost_account_code: form.cost_account_code,
        comment: form.comment || null,
        project_id: form.project_id,
        items: form.items,
      });
      try {
        await productionServiceActs.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/production-service-acts/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['production-service-acts'] });
      navigate(`/production-service-acts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });

  // Збережений непроведений: зберегти правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await productionServiceActs.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        customer_contractor_id: form.customer_contractor_id,
        contract_id: form.contract_id || null,
        advance_offset_mode: form.advance_offset_mode,
    customer_order_id: form.customer_order_id || null,
        service_amount: form.service_amount,
        vat_amount: form.vat_amount,
        revenue_account_code: form.revenue_account_code,
        receivable_account_code: form.receivable_account_code,
        cost_account_code: form.cost_account_code,
        comment: form.comment || null,
        project_id: form.project_id,
        items: form.items,
      });
      await productionServiceActs.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-service-act', id] });
      qc.invalidateQueries({ queryKey: ['production-service-acts'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  const unpost = useMutation({
    mutationFn: () => productionServiceActs.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-service-act', id] });
      qc.invalidateQueries({ queryKey: ['production-service-acts'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  // ч.701: видалення (кнопка на плашці; для new/posted — disabled).
  const remove = useMutation({
    mutationFn: () => productionServiceActs.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-service-acts'] });
      navigate('/production-service-acts');
    },
    onError: (e) => setError(extractError(e)),
  });
  function removeItem(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items
        .filter((_, idx) => idx !== i)
        .map((it, idx) => ({ ...it, line_number: idx + 1 })),
    }));
  }
  function setItemField(i: number, patch: Partial<ProductionServiceActItemIn>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, idx) => {
        if (idx !== i) return it;
        const next = { ...it, ...patch };
        // ч.699: авто-ПДВ рядка від ставки (ручна правка vat_amount — як є).
        if (!('vat_amount' in patch)
            && next.vat_rate_percent != null && next.vat_rate_percent !== '') {
          next.vat_amount = (lineAmount(next) * Number(next.vat_rate_percent) / 100).toFixed(2);
        }
        return next;
      }),
    }));
  }

  // ч.695: ПН з акта (той самий механізм base_document_*, що у реалізації).
  const [pnBusy, setPnBusy] = useState(false);
  const pnListQ = useQuery({
    queryKey: ['pn-for-act', id],
    queryFn: () => taxInvoicesOutgoing.list({ limit: 500 }),
    enabled: !isNew && isPosted,
  });
  const existingPn = useMemo(() => {
    return (pnListQ.data?.items ?? [])
      .find((p) => p.base_document_id === id && !p.deletion_mark) ?? null;
  }, [pnListQ.data, id]);
  async function createPnFromAct() {
    if (!id) return;
    setPnBusy(true);
    try {
      const pn = await taxInvoicesOutgoing.createFromSale({
        organization_id: form.organization_id,
        base_document_type: 'doc.production_service_acts',
        base_document_id: id,
      });
      navigate(`/tax-invoices-outgoing/${pn.id}`);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setPnBusy(false);
    }
  }
  // ч.695: «заповнити з рахунку» — рядки-послуги з проведеного рахунку.
  const [invoicePickOpen, setInvoicePickOpen] = useState(false);
  const invoicesQ = useQuery({
    queryKey: ['invoices-for-act', form.organization_id, form.customer_contractor_id],
    queryFn: () => customerOrders.list({
      organization_id: form.organization_id,
      contractor_id: form.customer_contractor_id || undefined,
      order_kind: 'PROFORMA_INVOICE', posted: true, limit: 100,
    }),
    enabled: invoicePickOpen && !!form.organization_id,
  });
  const nomAllQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => import('@/api/catalogs').then((m) => m.nomenclature.listAll()),
  });
  async function fillFromInvoice(orderId: string) {
    setError(null);
    try {
      const order = await customerOrders.get(orderId);
      const svcIds = new Set(
        (nomAllQ.data?.items ?? []).filter((n) => n.is_service).map((n) => n.id));
      const rows = order.items
        .filter((it) => it.nomenclature_id && svcIds.has(it.nomenclature_id))
        .map((it, idx) => {
          // ч.699: ПДВ рядка з рахунку; ставка — хіт по співвідношенню.
          const amt = Number(it.amount_without_vat) || 0;
          const vat = Number(it.vat_amount) || 0;
          const ratio = amt > 0 ? vat / amt * 100 : 0;
          const hit = [20, 14, 7, 0].find((c) => Math.abs(ratio - c) < 0.5);
          return {
            line_number: idx + 1,
            nomenclature_id: it.nomenclature_id!,
            quantity: it.quantity,
            price: it.price,
            description: '',
            vat_rate_percent: hit != null ? String(hit) : null,
            vat_amount: vat.toFixed(2),
            tax_purpose_id: null,
            planned_amount: null,
            nomenclature_group_id: null,
          };
        });
      if (rows.length === 0) {
        setError('У рахунку немає рядків-послуг (акт — лише для послуг).');
        return;
      }
      setForm((f) => ({
        ...f,
        customer_contractor_id: order.contractor_id,
        contract_id: order.contract_id ?? '',
        customer_order_id: order.id,
        items: rows,
      }));
      setInvoicePickOpen(false);
    } catch (e) {
      setError(extractError(e));
    }
  }

  // ч.696: /production-service-acts/new?basis=<invoice_id> — «створити акт
  // послуг» з рахунку: шапка+рядки-послуги з підстави.
  const basisApplied = useRef(false);
  useEffect(() => {
    if (!isNew || !basisId || basisApplied.current) return;
    if (!nomAllQ.data) return; // фільтр послуг потребує довідник
    basisApplied.current = true;
    void fillFromInvoice(basisId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, basisId, nomAllQ.data]);

  // ч.698: рядки з попапа мультивибору (довідник ПОСЛУГ).
  function addRowsFromPicker(rows: GoodsBalanceRow[]) {
    setForm((f) => ({
      ...f,
      items: [...f.items, ...rows.map((r, k) => ({
        ...newRow(f.items.length + 1 + k),
        nomenclature_id: r.nomenclature_id,
      }))],
    }));
  }

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  // ч.862: «ще дії» → права колонка «Повʼязані дії» (обидві гілки ПН збережено).
  const railActions = [
    existingPn ? {
      label: 'відкрити ПН',
      title: `ПН №${existingPn.invoice_number} вже створена з цього акта`,
      onClick: () => navigate(`/tax-invoices-outgoing/${existingPn.id}`),
    } : {
      label: pnBusy ? 'ПН створюється…' : 'створити ПН',
      title: isNew
        ? 'Спершу збережіть акт'
        : 'Створити податкову накладну на основі цього акта',
      disabled: pnBusy || isNew,
      onClick: () => void createPnFromAct(),
    },
  ];
  // ч.862: прогрес — ЛИШЕ обовʼязкові поля.
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'party', title: 'Замовник і договір', required: 1,
      filled: form.customer_contractor_id ? 1 : 0 },
    { id: 'items', title: 'Послуги', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      {/* ч.862 (новий дизайн, Закон #10): блоки + права рейка. */}
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий акт виробничих послуг' : `Акт: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          entriesDoc={{ type: 'doc.production_service_acts', id: isNew ? '' : id! }}
          onBackToList={() => navigate('/production-service-acts')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => (isNew ? createAndPost.mutate() : updateAndPost.mutate())}
          postPending={create.isPending || createAndPost.isPending || update.isPending || updateAndPost.isPending}
          postTitle="Зберігає та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { void confirm('Видалити акт?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/production-service-acts/${id}/print`)}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>


      {/* ч.698 (візуал goods-sales): [Юрособа|Номер|Дата] →
          [Замовник*|Договір(+створити)|заповнити з рахунку + Підстава] →
          Коментар; вкладки Послуги | Рахунки обліку; суми — З РЯДКІВ. */}
      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.production_service_acts', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid" style={{ marginBottom: '0.75rem' }}>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name} — {o.code}
              </option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Номер</label>
          <input
            value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
            placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            disabled={isPosted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата *</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted}
          />
        </div>
        <ProjectField span={2}
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>
      </DocBlock>

      <DocBlock index={2} title="Замовник і договір">
      <div className="doc-grid" style={{ marginBottom: '0.75rem' }}>
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>Замовник *
            <QuickCreateLink title="Створити нового контрагента"
              disabled={isPosted}
              onClick={() => setQuickContractor(true)} />
          </label>
          <SearchSelect
            items={(cQ.data?.items ?? []).map((c) => ({ id: c.id, label: c.name, hint: c.edrpou ?? null }))}
            value={form.customer_contractor_id}
            onChange={(v) => setForm({ ...form, customer_contractor_id: v, contract_id: '' })}
            disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>
            Договір{' '}
            <QuickCreateLink
              disabled={isPosted || !form.customer_contractor_id}
              title={form.customer_contractor_id ? 'Створити договір цього контрагента' : 'Спершу оберіть контрагента'}
              onClick={() => setQuickContract(true)} />
          </label>
          <select value={form.contract_id}
                  onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                  disabled={isPosted || !form.customer_contractor_id}>
            <option value="">без договору</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{contractLabel(c)}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          {/* ч.862: «Підстава» — назва поля, документ під нею; кнопка — лише
              поки звʼязку немає (як у рахунку/реалізації). */}
          <label>{form.customer_order_id ? 'Підстава' : '\u00A0'}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {!form.customer_order_id && (
            <button type="button" className="btn-doc"
              onClick={() => setInvoicePickOpen(true)}
              disabled={isPosted || !form.organization_id}
              title={!form.organization_id
                  ? 'Спершу оберіть юрособу'
                  : 'Заповнити акт рядками-послугами з проведеного рахунку покупцю'}>
              <DocIcon />заповнити з рахунку
            </button>
            )}
            {form.customer_order_id && (
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
                  onClick={() => navigate(`/customer-invoices/${form.customer_order_id}`)}>
                  рахунок покупцю
                </a>
                {!isPosted && (
                  <button type="button" className="btn-x"
                    onClick={() => setForm((f) => ({ ...f, customer_order_id: '' }))}
                    title="Зняти звʼязок з рахунком">×</button>
                )}
              </span>
            )}
          </div>
        </div>
      </div>
      {/* ч.1193: залік авансу замовника — як на Реалізації. */}
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isPosted ? 'default' : 'pointer', margin: '0.5rem 0 0' }}>
        <input type="checkbox"
          checked={form.advance_offset_mode === 'NONE'}
          disabled={isPosted}
          onChange={(e) => setForm({ ...form, advance_offset_mode: e.target.checked ? 'NONE' : 'AUTO' })} />
        <span>не зараховувати аванс автоматично
          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginLeft: '0.4rem' }}>
            (типово аванс за договором зараховується при проведенні)
          </span>
        </span>
      </label>
      </DocBlock>

      <DocBlock index={3} title="Послуги"
        actions={!isPosted ? (<>
          <button onClick={() => setQuickNomOpen(true)} className="btn-doc"
            title="Створити нову номенклатуру-послугу і одразу підставити рядком">
            <DocIcon />створити номенклатуру
          </button>
          <button className="primary" onClick={() => setPickerOpen(true)}
            disabled={!form.organization_id}
            title={!form.organization_id ? 'Спершу оберіть юрособу'
              : 'Підбір послуги з довідника (мультивибір)'}>
            + додати послугу
          </button>
        </>) : undefined}>
      {/* Вкладки — канон реалізації: Послуги зліва, Рахунки обліку справа. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div className="seg-tabs">
          <button type="button" className={viewTab === 'lines' ? 'active' : ''}
            onClick={() => setViewTab('lines')}>Послуги ({form.items.length})</button>
        </div>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={viewTab === 'accounts' ? 'active' : ''}
            onClick={() => setViewTab('accounts')}>Рахунки обліку</button>
          <button type="button" className={viewTab === 'tax' ? 'active' : ''}
            onClick={() => setViewTab('tax')}>Податковий облік</button>
        </div>
      </div>

      {viewTab === 'accounts' && (
        <div className="doc-grid" style={{ marginTop: '0.75rem' }}>
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Рахунок доходів</label>
            {acctSelect(form.revenue_account_code, (v) => setForm({ ...form, revenue_account_code: v }), '703')}
          </div>
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Рахунок розрахунків</label>
            {acctSelect(form.receivable_account_code, (v) => setForm({ ...form, receivable_account_code: v }), '361')}
          </div>
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Рахунок затрат</label>
            {acctSelect(form.cost_account_code, (v) => setForm({ ...form, cost_account_code: v }), '231')}
          </div>
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label title="Підрозділ котла 23 — планова собівартість послуг списується з нього (Дт903—Кт23); закриття місяця доводить до факту">
              Підрозділ (котел 23)
            </label>
            <select value={form.department_id}
                    onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                    disabled={isPosted}>
              <option value="">—</option>
              {(depsQ.data?.items ?? []).map((dp) => (
                <option key={dp.id} value={dp.id}>{dp.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {viewTab === 'tax' && (
        <TchTable style={{ marginTop: '0.5rem' }}>
          <thead>
            <tr style={{ textTransform: 'none' }}>
              <th>Номенклатура</th>
              <th style={{ width: 160 }}>Ставка ПДВ</th>
              <th style={{ width: 220 }}
                  title="Призначення ПДВ для пропорційного розподілу (ПКУ ст.199)">Призначення ПДВ</th>
            </tr>
          </thead>
          <tbody>
            {form.items.filter((it) => it.nomenclature_id).map((it) => {
              const i = form.items.indexOf(it);
              const nom = nomAllQ.data?.items.find((n) => n.id === it.nomenclature_id);
              return (
                <tr key={i}>
                  <td>{nom?.name ?? '—'}</td>
                  <td>
                    <select value={it.vat_rate_percent ?? ''}
                            onChange={(e) => setItemField(i, { vat_rate_percent: e.target.value || null })}
                            disabled={isPosted} style={{ width: '100%' }}>
                      <option value="">без ПДВ</option>
                      <option value="20">20%</option>
                      <option value="14">14%</option>
                      <option value="7">7%</option>
                      <option value="0">0%</option>
                    </select>
                  </td>
                  <td>
                    <select value={it.tax_purpose_id ?? ''}
                            onChange={(e) => setItemField(i, { tax_purpose_id: e.target.value || null })}
                            disabled={isPosted} style={{ width: '100%' }}>
                      <option value="">—</option>
                      {taxPurposesQ.data?.map((tp) => (
                        <option key={tp.id} value={tp.id}>{tp.name_uk}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TchTable>
      )}
      {viewTab === 'lines' && (<>
      <TchTable style={{ marginTop: '0.5rem' }}>
        <thead>
          <tr style={{ textTransform: 'none' }}>
            <th style={{ width: 40 }}>#</th>
            <th>Номенклатура</th>
            <th title="Текст у друк акта наданих послуг">Зміст послуги</th>
            <th style={{ width: 90 }}>К-сть</th>
            <th style={{ width: 110 }}>Ціна</th>
            <th style={{ width: 100 }}
                title="Планова собівартість послуги (як 1С «Сума планова»): при проведенні списується Дт903—Кт23, закриття місяця доводить до факту. Порожньо — собівартість не списується">
              Планова с/в
            </th>
            <th style={{ width: 130 }}>Сума без ПДВ</th>
            <th style={{ width: 100 }}>ПДВ</th>
            <th style={{ width: 110 }}>Сума ПДВ</th>
            <th style={{ width: 120 }}>Разом</th>
            <th style={{ width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i}>
              <td>{it.line_number}</td>
              <td>
                <NomenclatureSelect
                  value={it.nomenclature_id}
                  disabled={isPosted}
                  onChange={(v) => setItemField(i, { nomenclature_id: v })}
                  kind="services"
                />
              </td>
              <td>
                <input value={it.description ?? ''}
                       onChange={(e) => setItemField(i, { description: e.target.value })}
                       disabled={isPosted} maxLength={500}
                       placeholder="текст у друк акта" style={{ width: '100%' }} />
              </td>
              <td>
                <input
                  type="number"
                  step="0.001"
                  value={it.quantity}
                  onChange={(e) => setItemField(i, { quantity: e.target.value })}
                  disabled={isPosted}
                />
              </td>
              <td>
                <input
                  type="number"
                  step="0.01"
                  value={it.price}
                  onChange={(e) => setItemField(i, { price: e.target.value })}
                  disabled={isPosted}
                />
              </td>
              <td>
                <input type="number" step="0.01" value={it.planned_amount ?? ''}
                       onChange={(e) => setItemField(i, { planned_amount: e.target.value || null })}
                       disabled={isPosted} placeholder="—"
                       style={{ textAlign: 'right' }} />
              </td>
              <td style={{ textAlign: 'right' }}>{lineAmount(it).toFixed(2)}</td>
              <td>
                <select value={it.vat_rate_percent ?? ''}
                        onChange={(e) => setItemField(i, { vat_rate_percent: e.target.value || null })}
                        disabled={isPosted}>
                  <option value="">без ПДВ</option>
                  <option value="20">20%</option>
                  <option value="14">14%</option>
                  <option value="7">7%</option>
                  <option value="0">0%</option>
                </select>
              </td>
              <td>
                <input type="number" step="0.01" value={it.vat_amount ?? '0'}
                       onChange={(e) => setItemField(i, { vat_amount: e.target.value })}
                       disabled={isPosted}
                       style={{ textAlign: 'right' }} />
              </td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>
                {(lineAmount(it) + (Number(it.vat_amount) || 0)).toFixed(2)}
              </td>
              <td>
                {!isPosted && (
                  <button onClick={() => removeItem(i)} className="danger"
                          style={{ padding: '0.3rem 0.6rem' }} title="Видалити">
                    ×
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        {form.items.length > 0 && (
          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={6} style={{ textAlign: 'right' }}>Разом:</td>
              <td style={{ textAlign: 'right' }}>{totals.svc.toFixed(2)}</td>
              <td />
              <td style={{ textAlign: 'right' }}>{totals.vat.toFixed(2)}</td>
              <td style={{ textAlign: 'right' }}>{totals.total.toFixed(2)}</td>
              <td />
            </tr>
          </tfoot>
        )}
      </TchTable>

      </>)}

      {/* ч.698: попап підбору послуг (мультивибір) + швидке створення. */}
      {pickerOpen && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          catalogMode
          onlyKind="services"
          multiSelect
          onPick={(row) => addRowsFromPicker([row])}
          onPickMany={addRowsFromPicker}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {quickNomOpen && (
        <QuickNomenclatureModal
          onClose={() => setQuickNomOpen(false)}
          onCreated={(nom) => {
            setQuickNomOpen(false);
            addRowsFromPicker([{
              warehouse_id: '', warehouse_code: '', warehouse_name: '',
              nomenclature_id: nom.id, nomenclature_code: nom.code,
              nomenclature_article: null, nomenclature_name: nom.name,
              quantity: '0', cost: null,
            } as GoodsBalanceRow]);
          }}
        />
      )}

      {/* ч.695: вибір проведеного рахунку для заповнення акта послугами. */}
      {invoicePickOpen && (
        <div onClick={() => setInvoicePickOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'var(--overlay)',
            zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--bg-panel)', borderRadius: 8, width: 640,
              maxHeight: '70vh', overflow: 'auto', padding: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Рахунки покупцю</h3>
              <button onClick={() => setInvoicePickOpen(false)}
                style={{ marginLeft: 'auto', padding: '0.3rem 0.6rem' }}>×</button>
            </div>
            {(invoicesQ.data?.items ?? []).length === 0 ? (
              <p style={{ color: 'var(--muted)' }}>Немає проведених рахунків.</p>
            ) : (
              <TchTable>
                <thead>
                  <tr>
                    <th>Номер</th>
                    <th style={{ width: 110 }}>Дата</th>
                    <th style={{ width: 130 }}>Сума</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoicesQ.data?.items ?? []).map((o) => (
                    <tr key={o.id} onClick={() => fillFromInvoice(o.id)}
                        style={{ cursor: 'pointer' }}
                        title="Заповнити акт рядками-послугами цього рахунку">
                      <td>{o.document_number}</td>
                      <td>{o.document_date.slice(0, 10)}</td>
                      <td style={{ textAlign: 'right' }}>{o.total_amount}</td>
                    </tr>
                  ))}
                </tbody>
              </TchTable>
            )}
          </div>
        </div>
      )}

      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            disabled={isPosted}
          />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType={'doc.production_service_acts'} documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && (
        <div className="error" style={{ marginTop: '1rem' }}>
          {error}
        </div>
      )}
      {/* ч.948: попапи швидкого створення просто з форми документа. */}
      {quickContractor && (
        <QuickContractorModal
          direction={'IN'}
          onClose={() => setQuickContractor(false)}
          onCreated={(cid) => {
            setQuickContractor(false);
            setForm((f) => ({ ...f, customer_contractor_id: cid, contract_id: '' }));
            qc.invalidateQueries({ queryKey: ['contractors'] });
          }} />
      )}
      {quickContract && form.customer_contractor_id && (
        <QuickContractModal
          orgId={form.organization_id}
          contractorId={form.customer_contractor_id}
          contractorName={contractorNameForQuick}
          direction={'IN'}
          onClose={() => setQuickContract(false)}
          onCreated={(cid) => {
            setQuickContract(false);
            setForm((f) => ({ ...f, contract_id: cid }));
            qc.invalidateQueries({ queryKey: ['contracts', form.organization_id, form.customer_contractor_id] });
          }} />
      )}
    </div>
  );
}
